import Vue from "vue";
import i18n from "../lang/index.js";
import GlobalErrorModal from "../components/elements/globalErrorModal.vue";

// Single, lazily-mounted modal instance shared across the whole app.
let modal = null;
function getModal() {
  if (!modal) {
    const Ctor = Vue.extend(GlobalErrorModal);
    modal = new Ctor({ i18n });
    const el = document.createElement("div");
    document.body.appendChild(el);
    modal.$mount(el);
  }
  return modal;
}

function t(key, fallback) {
  const v = i18n.t(key);
  return v === key ? fallback : v;
}
function friendly() {
  return t(
    "ServerError",
    "Serverda kutilmagan xatolik yuz berdi. Iltimos, keyinroq qayta urinib ko'ring."
  );
}

// Markers that indicate a raw server stack-trace / dump rather than a
// human-readable message.
const STACK_RE = /System\.|Exception|StackTrace|CancellationToken|\n\s*at\s/i;
const MAX_TOAST_LEN = 220;

function toStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch (e) {
    return String(v);
  }
}

function classifyString(s) {
  if (!s) return { modal: false, message: "" };
  if (s.length > MAX_TOAST_LEN || STACK_RE.test(s)) {
    return { modal: true, message: friendly(), details: s };
  }
  return { modal: false, message: s };
}

// Flatten a { field: ["msg", ...] | "msg" } dictionary into a flat array of
// human-readable strings.
function collectMessages(obj) {
  const out = [];
  Object.values(obj).forEach((v) => {
    if (Array.isArray(v)) v.forEach((x) => x != null && out.push(String(x)));
    else if (typeof v === "string") out.push(v);
  });
  return out;
}

function numberedList(messages) {
  return messages.map((e, i) => i + 1 + ". " + e).join("\n");
}

// Does this object look like an ASP.NET ModelState validation dictionary
// (a plain map of field -> array-of-messages / message) returned on a 400,
// rather than a 500 stack dump / problem-details payload?
function isValidationDict(obj) {
  const vals = Object.values(obj);
  if (!vals.length) return false;
  return vals.every(
    (v) =>
      (Array.isArray(v) && v.every((x) => typeof x === "string")) ||
      typeof v === "string"
  );
}

// Decide whether a given message should be a small toast or the rich modal,
// and normalise it into a clean message / details pair.
function classify(message, type) {
  if (type && type !== "error") {
    return { modal: false, message: toStr(message) };
  }
  if (message && typeof message === "object") {
    // Validation errors: { errors: { field: ["..."] } } -> numbered list
    if (message.errors && typeof message.errors === "object") {
      return classifyString(numberedList(collectMessages(message.errors)));
    }
    // 500 / problem-details style: friendly message + raw dump in details
    if (message.status == 500 || message.title || message.detail) {
      return {
        modal: true,
        message: friendly(),
        details: message.title || message.detail || toStr(message),
      };
    }
    if (typeof message.error === "string") return classifyString(message.error);
    if (typeof message.message === "string")
      return classifyString(message.message);
    // Raw 400 validation dictionary returned straight from the backend, e.g.
    // { EndDate: ["..."], StartDate: ["..."] } -> show those messages instead
    // of the generic "unexpected error" modal + JSON dump.
    if (isValidationDict(message)) {
      return classifyString(numberedList(collectMessages(message)));
    }
    return { modal: true, message: friendly(), details: toStr(message) };
  }
  return classifyString(toStr(message));
}

export default {
  install(VueCtor) {
    const original = VueCtor.prototype.$toast;
    const originalOpen =
      original && typeof original.open === "function"
        ? original.open.bind(original)
        : null;

    function route(options) {
      const opts =
        typeof options === "string" ? { message: options } : options || {};
      const r = classify(opts.message, opts.type);
      if (r.modal) {
        getModal().showError(r.message, r.details);
        return;
      }
      if (originalOpen && r.message) {
        originalOpen(Object.assign({}, opts, { message: r.message }));
      }
    }

    // Wrap $toast.open so every existing makeToast() call benefits with no
    // changes at the call sites. Other $toast.* methods are preserved.
    const wrapper = Object.create(original || {});
    wrapper.open = route;
    VueCtor.prototype.$toast = wrapper;

    // Convenience helper for handling a raw axios error directly.
    VueCtor.prototype.$showError = function(error) {
      let data = error;
      if (error && error.response) data = error.response.data;
      else if (error instanceof Error) data = error.message;
      route({ message: data, type: "error" });
    };
  },
};
